import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { GetReservationNotesAPI } from "api/guest";
import { DeleteReservationNoteAPI } from "api/guest";
import { GetGuestLibraryAPI } from "api/guest";
import { GetGuestStaysAPI } from "api/guest";
import { GetGuestReservationNotesAPI } from "api/guest";
import { GetGuestDetailsAPI } from "api/guest";
import { GetReservationSummaryAPI } from "api/guest";
import { CreateReservationNoteAPI } from "api/guest";
import { SetGuestFlagAPI } from "api/guest";
import { GetGuestFlagsAPI } from "api/guest";
import { GetInHouseGuestsAPI } from "api/guest";
import {
  CreateGuestProfileNoteAPI,
  DeleteGuestAPI,
  GetRemovedGuestDeletionJobsAPI,
  GetRemovedGuestDeletionSummaryAPI,
  RetryGuestDeletionJobAPI,
  UpdateGuestProfileNoteAPI,
} from "../../api/guest";
import { generateReservationSummary } from "./arrivalsSlice";

export const getInHouseGuests = createAsyncThunk(
  "guest/getInHouseGuests",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await GetInHouseGuestsAPI(payload);

      return response?.data?.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error.message || "Failed to fetch guests"
      );
    }
  }
);

export const getGuestFlags = createAsyncThunk(
  "guest/getGuestFlags",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await GetGuestFlagsAPI(payload);
      return response?.data?.data?.flags || [];
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error.message || "Failed to fetch flags"
      );
    }
  }
);

export const setGuestFlag = createAsyncThunk(
  "guest/setGuestFlag",
  async ({ hotelId, guestId, guestFlag, guestFlagReason }, { rejectWithValue }) => {
    try {
      const response = await SetGuestFlagAPI({
        hotelId,
        guestId,
        data: {
          guestFlag,
          guestFlagReason,
        },
      });

      return response?.data?.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error.message || "Failed to update guest flag"
      );
    }
  }
);

export const getReservationNotes = createAsyncThunk(
  "guest/getReservationNotes",
  async ({ hotelId, guestId, reservationId, query }, { rejectWithValue }) => {
    try {
      const response = await GetReservationNotesAPI({
        hotelId,
        guestId,
        reservationId,
        query,
      });

      return response?.data?.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error.message || "Failed to fetch notes"
      );
    }
  }
);

export const deleteReservationNote = createAsyncThunk(
  "guest/deleteReservationNote",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await DeleteReservationNoteAPI(payload);

      return {
        ...payload,
        staffNotesCount: response?.data?.data?.staffNotesCount ?? 0,
      };
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error.message || "Failed to delete note"
      );
    }
  }
);

export const createReservationNote = createAsyncThunk(
  "guest/createReservationNote",
  async ({ hotelId, guestId, reservationId, data }, { rejectWithValue }) => {
    try {
      const response = await CreateReservationNoteAPI({
        hotelId,
        guestId,
        reservationId,
        data,
      });

      return response?.data?.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error.message || "Failed to create note"
      );
    }
  }
);

export const getReservationSummary = createAsyncThunk(
  "guest/getReservationSummary",
  async ({ hotelId, guestId, reservationId }, { rejectWithValue }) => {
    try {
      const response = await GetReservationSummaryAPI({
        hotelId,
        guestId,
        reservationId,
      });

      return response.data.data.summary;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error.message || "Failed to fetch summary"
      );
    }
  }
);

export const getGuestLibrary = createAsyncThunk(
  "guest/getGuestLibrary",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await GetGuestLibraryAPI(payload);

      return response?.data?.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error.message || "Failed to fetch guest library"
      );
    }
  }
);

export const getGuestDetails = createAsyncThunk(
  "guest/getGuestDetails",
  async ({ guestId, hotelIds }, { rejectWithValue }) => {
    try {
      const response = await GetGuestDetailsAPI({
        guestId,
        data: { hotelIds },
      });

      return response?.data?.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || "Failed to fetch guest details");
    }
  }
);

export const getGuestStays = createAsyncThunk(
  "guest/getGuestStays",
  async ({ guestId, hotelIds, page, pageSize }, { rejectWithValue }) => {
    try {
      const response = await GetGuestStaysAPI({
        guestId,
        data: { hotelIds, page, pageSize },
      });

      return response?.data?.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || "Failed to fetch guest stays");
    }
  }
);

export const getGuestReservationNotes = createAsyncThunk(
  "guest/getGuestReservationNotes",
  async ({ guestId, hotelIds }, { rejectWithValue }) => {
    try {
      const response = await GetGuestReservationNotesAPI({
        guestId,
        data: { hotelIds },
      });

      return response?.data?.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || "Failed to fetch guest notes");
    }
  }
);

export const deleteGuest = createAsyncThunk(
  "guest/deleteGuest",
  async (guestId, { rejectWithValue }) => {
    try {
      const response = await DeleteGuestAPI(guestId);

      return response?.data?.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error.message || "Failed to delete guest"
      );
    }
  }
);

export const createGuestProfileNote = createAsyncThunk(
  "guests/createGuestProfileNote",
  async ({ guestId, text, createdFromHotelId }, thunkAPI) => {
    try {
      const response = await CreateGuestProfileNoteAPI(guestId, {
        text,
        createdFromHotelId,
      });

      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || "Failed to create guest profile note."
      );
    }
  }
);

export const updateGuestProfileNote = createAsyncThunk(
  "guests/updateGuestProfileNote",
  async ({ guestId, noteId, text }, thunkAPI) => {
    try {
      const response = await UpdateGuestProfileNoteAPI(guestId, noteId, {
        text,
      });

      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || "Failed to update guest profile note."
      );
    }
  }
);

export const getRemovedGuestDeletionJobs = createAsyncThunk(
  "guest/getRemovedGuestDeletionJobs",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await GetRemovedGuestDeletionJobsAPI(payload);

      return response?.data?.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error.message || "Failed to fetch deletion jobs"
      );
    }
  }
);

export const getRemovedGuestDeletionSummary = createAsyncThunk(
  "guest/getRemovedGuestDeletionSummary",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await GetRemovedGuestDeletionSummaryAPI(payload);

      return response?.data?.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error.message || "Failed to fetch deletion summary"
      );
    }
  }
);

export const retryGuestDeletionJob = createAsyncThunk(
  "guest/retryGuestDeletionJob",
  async (jobId, { rejectWithValue }) => {
    try {
      const response = await RetryGuestDeletionJobAPI(jobId);

      return response?.data?.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error.message || "Failed to retry deletion job"
      );
    }
  }
);

// Overlay requests use the existing thunks without replacing the full history page's data.
const updateProfileView = (state, action, resource, status) => {
  const view = action.meta.arg.profileView;
  if (!view) return false;
  if (status === "pending") {
    state.profileViews[view] ||= {};
    state.profileViews[view][resource] = {
      requestId: action.meta.requestId, loading: true, error: null, data: null,
    };
  } else {
    const request = state.profileViews[view]?.[resource];
    if (request?.requestId === action.meta.requestId) {
      request.loading = false;
      request.data = status === "fulfilled" ? action.payload : null;
      request.error = status === "rejected" && !action.meta.aborted
        ? action.payload || action.error?.message || "Unable to load guest profile"
        : null;
    }
  }
  return true;
};

const initialState = {
  profileViews: {},
  guests: [],
  page: 0,
  pageSize: 10,
  total: 0,

  guestLibrary: [],
  guestLibraryPage: 0,
  guestLibraryPageSize: 20,
  guestLibraryTotal: 0,

  removedGuests: [],
  removedGuestsPage: 0,
  removedGuestsPageSize: 20,
  removedGuestsTotal: 0,

  flags: [],
  flagsLoading: false,
  flagsError: null,

  flagUpdating: false,
  flagUpdateError: null,
  flagUpdateSuccess: false,

  listLoading: false,
  listError: null,
  listSuccess: false,

  notes: [],
  notesLoading: false,
  notesError: null,

  createNoteLoading: false,
  createNoteError: null,
  createNoteSuccess: false,

  deleteNoteLoading: false,
  deleteNoteError: null,

  guestLibraryLoading: false,
  guestLibraryError: null,
  guestLibrarySuccess: false,

  deleteGuestLoading: false,
  deleteGuestError: null,
  deleteGuestSuccess: false,
  deletedGuestId: null,

  guestDetails: null,
  guestCounters: null,
  guestProfileNotes: [],

  guestStays: [],
  guestStaysTotal: 0,
  guestStaysPagination: null,
  guestStaysTruncated: false,

  guestNotes: {
    arrival_note: [],
    staff_note: [],
    pms_note: [],
  },
  guestNotesTotals: null,
  guestNotesTruncated: false,

  guestDetailsLoading: false,
  guestDetailsError: null,

  guestStaysLoading: false,
  guestStaysError: null,

  guestNotesLoading: false,
  guestNotesError: null,

  createGuestProfileNoteLoading: false,
  createGuestProfileNoteSuccess: false,
  createGuestProfileNoteError: null,

  updateGuestProfileNoteLoading: false,
  updateGuestProfileNoteSuccess: false,
  updateGuestProfileNoteError: null,

  removedGuestsLoading: false,
  removedGuestsError: null,

  removedGuestsSummary: null,
  removedGuestsSummaryLoading: false,
  removedGuestsSummaryError: null,

  retryGuestDeletionLoading: false,
  retryGuestDeletionError: null,
  retryGuestDeletionSuccess: false,
  retryGuestDeletionJobId: null,
};

/* =========================================================
   SLICE
========================================================= */
const guestSlice = createSlice({
  name: "guests",
  initialState,
  reducers: {
    resetGuestProfileView: (state, action) => {
      delete state.profileViews[action.payload];
    },
    resetGuestState: (state) => {
      state.listLoading = false;
      state.listError = null;
      state.listSuccess = false;
    },
    clearGuestError: (state) => {
      state.listError = null;
    },

    clearGuestFlags: (state) => {
      state.flags = [];
      state.flagsError = null;
    },

    resetGuestLibraryState: (state) => {
      state.guestLibraryLoading = false;
      state.guestLibraryError = null;
      state.guestLibrarySuccess = false;
    },

    resetFlagUpdateState: (state) => {
      state.flagUpdating = false;
      state.flagUpdateError = null;
      state.flagUpdateSuccess = false;
    },

    resetNotesState: (state) => {
      state.notes = [];
      state.notesLoading = false;
      state.notesError = null;
    },

    resetCreateNoteState: (state) => {
      state.createNoteLoading = false;
      state.createNoteError = null;
      state.createNoteSuccess = false;
    },

    resetGuestProfileState: (state) => {
      state.guestDetails = null;
      state.guestCounters = null;
      state.guestProfileNotes = [];

      state.guestStays = [];
      state.guestStaysTotal = 0;
      state.guestStaysPagination = null;
      state.guestStaysTruncated = false;

      state.guestNotes = {
        arrival_note: [],
        staff_note: [],
        pms_note: [],
      };

      state.guestNotesTotals = null;
      state.guestNotesTruncated = false;

      state.guestDetailsError = null;
      state.guestStaysError = null;
      state.guestNotesError = null;
    },

    resetDeleteGuestState: (state) => {
      state.deleteGuestLoading = false;
      state.deleteGuestError = null;
      state.deleteGuestSuccess = false;
      state.deletedGuestId = null;
    },

    resetCreateGuestProfileNoteState: (state) => {
      state.createGuestProfileNoteLoading = false;
      state.createGuestProfileNoteSuccess = false;
      state.createGuestProfileNoteError = null;
    },

    resetUpdateGuestProfileNoteState: (state) => {
      state.updateGuestProfileNoteLoading = false;
      state.updateGuestProfileNoteSuccess = false;
      state.updateGuestProfileNoteError = null;
    },
    resetRemovedGuestsState: (state) => {
      state.removedGuests = [];
      state.removedGuestsPage = 0;
      state.removedGuestsPageSize = 20;
      state.removedGuestsTotal = 0;
      state.removedGuestsLoading = false;
      state.removedGuestsError = null;
    },

    resetRetryGuestDeletionState: (state) => {
      state.retryGuestDeletionLoading = false;
      state.retryGuestDeletionError = null;
      state.retryGuestDeletionSuccess = false;
      state.retryGuestDeletionJobId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ===========================
      // GUEST LIST
      // ===========================
      .addCase(getInHouseGuests.pending, (state, action) => {
        state.inHouseRequestId = action.meta.requestId;
        state.guests = [];
        state.total = 0;
        state.listSuccess = false;
        state.listLoading = true;
        state.listError = null;
      })
      .addCase(getInHouseGuests.fulfilled, (state, action) => {
        if (state.inHouseRequestId !== action.meta.requestId) return;
        state.listLoading = false;
        state.listSuccess = true;

        state.guests = action.payload?.items || [];
        state.page = action.payload?.page || 0;
        state.pageSize = action.payload?.pageSize || 10;
        state.total = action.payload?.total || 0;
      })
      .addCase(getInHouseGuests.rejected, (state, action) => {
        if (state.inHouseRequestId !== action.meta.requestId) return;
        state.listLoading = false;
        state.listError = action.payload;
      })
      // ===========================
      // UPDATE AI SUMMARY
      // ===========================

      .addCase(generateReservationSummary.fulfilled, (state, action) => {
        const summary = action.payload?.data?.summary;
        const reservationId = action.meta.arg.reservationId;

        // find guest in list
        const guest = state.guests.find((g) => g.reservationId === reservationId);

        if (guest) {
          guest.aiSummary = summary;
        }
      })

      // ===========================
      // GUEST FLAGS
      // ===========================
      .addCase(getGuestFlags.pending, (state) => {
        state.flagsLoading = true;
        state.flagsError = null;
      })
      .addCase(getGuestFlags.fulfilled, (state, action) => {
        state.flagsLoading = false;
        state.flags = action.payload || [];
      })
      .addCase(getGuestFlags.rejected, (state, action) => {
        state.flagsLoading = false;
        state.flagsError = action.payload;
      })

      // ===========================
      // SET GUEST FLAG
      // ===========================
      .addCase(setGuestFlag.pending, (state) => {
        state.flagUpdating = true;
        state.flagUpdateError = null;
        state.flagUpdateSuccess = false;
      })
      .addCase(setGuestFlag.fulfilled, (state, action) => {
        state.flagUpdating = false;
        state.flagUpdateSuccess = true;

        const updated = action.payload;

        // Update guest inside list (IMPORTANT)
        const index = state.guests.findIndex((g) => g.guestId === updated.guestId);

        if (index !== -1) {
          state.guests[index].guestFlag = updated.guestFlag;
          state.guests[index].guestFlagReason = updated.guestFlagReason;
          state.guests[index].guestFlagUpdatedAt = updated.guestFlagUpdatedAt;
        }
      })
      .addCase(setGuestFlag.rejected, (state, action) => {
        state.flagUpdating = false;
        state.flagUpdateError = action.payload;
      })

      // ===========================
      // GET RESERVATION NOTES
      // ===========================
      .addCase(getReservationNotes.pending, (state) => {
        state.notesLoading = true;
        state.notesError = null;
      })
      .addCase(getReservationNotes.fulfilled, (state, action) => {
        state.notesLoading = false;

        state.notes = action.payload?.notes || [];
      })
      .addCase(getReservationNotes.rejected, (state, action) => {
        state.notesLoading = false;
        state.notesError = action.payload;
      })

      // ===========================
      // CREATE NOTE
      // ===========================
      .addCase(createReservationNote.pending, (state) => {
        state.createNoteLoading = true;
        state.createNoteError = null;
        state.createNoteSuccess = false;
      })
      .addCase(createReservationNote.fulfilled, (state, action) => {
        state.createNoteLoading = false;
        state.createNoteSuccess = true;

        const { note, staffNotesCount } = action.payload || {};

        // Add note on top
        if (note) {
          state.notes.unshift(note);
        }

        // Update guest
        const guestIndex = state.guests.findIndex(
          (g) => g.guestId === note?.guestId && g.reservationId === note?.reservationId
        );

        if (guestIndex !== -1) {
          state.guests[guestIndex].staffNotesCount = staffNotesCount ?? 0;
        }
      })
      .addCase(createReservationNote.rejected, (state, action) => {
        state.createNoteLoading = false;
        state.createNoteError = action.payload;
      })
      // ===========================
      // DELETE NOTE
      // ===========================
      .addCase(deleteReservationNote.pending, (state) => {
        state.deleteNoteLoading = true;
        state.deleteNoteError = null;
      })
      .addCase(deleteReservationNote.fulfilled, (state, action) => {
        state.deleteNoteLoading = false;

        const { noteId, guestId, reservationId, staffNotesCount } = action.payload;

        state.notes = state.notes.filter((n) => n._id !== noteId);

        const guest = state.guests.find(
          (g) => g.guestId === guestId && g.reservationId === reservationId
        );

        if (guest) {
          guest.staffNotesCount = staffNotesCount;
        }
      })
      .addCase(deleteReservationNote.rejected, (state, action) => {
        state.deleteNoteLoading = false;
        state.deleteNoteError = action.payload;
      })

      // ===========================
      // GUEST LIBRARY LIST
      // ===========================
      .addCase(getGuestLibrary.pending, (state) => {
        state.guestLibraryLoading = true;
        state.guestLibraryError = null;
        state.guestLibrarySuccess = false;
      })

      .addCase(getGuestLibrary.fulfilled, (state, action) => {
        state.guestLibraryLoading = false;
        state.guestLibrarySuccess = true;

        state.guestLibrary = action.payload?.items || [];
        state.guestLibraryPage = action.payload?.page || 0;
        state.guestLibraryPageSize = action.payload?.pageSize || 20;
        state.guestLibraryTotal = action.payload?.total || 0;
      })

      .addCase(getGuestLibrary.rejected, (state, action) => {
        state.guestLibraryLoading = false;
        state.guestLibraryError = action.payload;
      })

      // DETAILS
      .addCase(getGuestDetails.pending, (state, action) => {
        if (updateProfileView(state, action, "details", "pending")) return;
        state.guestDetailsLoading = true;
        state.guestDetailsError = null;
      })
      .addCase(getGuestDetails.fulfilled, (state, action) => {
        if (updateProfileView(state, action, "details", "fulfilled")) return;
        state.guestDetailsLoading = false;

        state.guestDetails = action.payload?.guest
          ? { ...action.payload.guest, badges: action.payload.guest.badges ?? action.payload.badges }
          : null;
        state.guestCounters = action.payload?.counters || null;
        state.guestProfileNotes = action.payload?.profileNotes || [];
      })
      .addCase(getGuestDetails.rejected, (state, action) => {
        if (updateProfileView(state, action, "details", "rejected")) return;
        state.guestDetailsLoading = false;
        state.guestDetailsError = action.payload;
      })

      // STAYS
      .addCase(getGuestStays.pending, (state, action) => {
        if (updateProfileView(state, action, "stays", "pending")) return;
        state.guestStaysLoading = true;
        state.guestStaysError = null;
      })
      .addCase(getGuestStays.fulfilled, (state, action) => {
        if (updateProfileView(state, action, "stays", "fulfilled")) return;
        state.guestStaysLoading = false;

        state.guestStays = action.payload?.stays || [];
        state.guestStaysTotal = action.payload?.total || 0;
        state.guestStaysPagination = action.payload?.pagination || null;
        state.guestStaysTruncated = action.payload?.truncated || false;
      })
      .addCase(getGuestStays.rejected, (state, action) => {
        if (updateProfileView(state, action, "stays", "rejected")) return;
        state.guestStaysLoading = false;
        state.guestStaysError = action.payload;
      })

      // NOTES
      .addCase(getGuestReservationNotes.pending, (state) => {
        state.guestNotesLoading = true;
        state.guestNotesError = null;
      })
      .addCase(getGuestReservationNotes.fulfilled, (state, action) => {
        state.guestNotesLoading = false;

        state.guestNotes = action.payload?.notesByCategory || {
          arrival_note: [],
          staff_note: [],
          pms_note: [],
        };

        state.guestNotesTotals = action.payload?.totals || null;
        state.guestNotesTruncated = action.payload?.truncated || false;
      })
      .addCase(getGuestReservationNotes.rejected, (state, action) => {
        state.guestNotesLoading = false;
        state.guestNotesError = action.payload;
      })

      // DELETE GUEST
      .addCase(deleteGuest.pending, (state) => {
        state.deleteGuestLoading = true;
        state.deleteGuestError = null;
        state.deleteGuestSuccess = false;
      })

      .addCase(deleteGuest.fulfilled, (state, action) => {
        state.deleteGuestLoading = false;
        state.deleteGuestSuccess = true;

        const deletedId = action.payload?.guestId;

        state.deletedGuestId = deletedId;

        // remove instantly from guest library table
        state.guestLibrary = (state.guestLibrary || []).filter((g) => g.guestId !== deletedId);

        state.guestLibraryTotal = Math.max(0, (state.guestLibraryTotal || 1) - 1);
      })

      .addCase(deleteGuest.rejected, (state, action) => {
        state.deleteGuestLoading = false;
        state.deleteGuestError = action.payload;
      })

      // CREATE GUEST PROFILE NOTE
      .addCase(createGuestProfileNote.pending, (state) => {
        state.createGuestProfileNoteLoading = true;
        state.createGuestProfileNoteSuccess = false;
        state.createGuestProfileNoteError = null;
      })
      .addCase(createGuestProfileNote.fulfilled, (state, action) => {
        state.createGuestProfileNoteLoading = false;
        state.createGuestProfileNoteSuccess = true;
        state.createGuestProfileNoteError = null;

        const createdNote = action.payload?.data?.note;

        if (createdNote) {
          if (!state.guestDetails) state.guestDetails = {};
          state.guestProfileNotes = [
            {
              noteId: createdNote.noteId || createdNote._id,
              ...createdNote,
            },
          ];
        }
      })
      .addCase(createGuestProfileNote.rejected, (state, action) => {
        state.createGuestProfileNoteLoading = false;
        state.createGuestProfileNoteSuccess = false;
        state.createGuestProfileNoteError =
          action.payload || "Failed to create guest profile note.";
      })

      // UPDATE GUEST PROFILE NOTE
      .addCase(updateGuestProfileNote.pending, (state) => {
        state.updateGuestProfileNoteLoading = true;
        state.updateGuestProfileNoteSuccess = false;
        state.updateGuestProfileNoteError = null;
      })
      .addCase(updateGuestProfileNote.fulfilled, (state, action) => {
        state.updateGuestProfileNoteLoading = false;
        state.updateGuestProfileNoteSuccess = true;
        state.updateGuestProfileNoteError = null;

        const updatedNote = action.payload?.data?.note;

        if (updatedNote) {
          if (!state.guestDetails) state.guestDetails = {};
          state.guestProfileNotes = [
            {
              noteId: updatedNote.noteId || updatedNote._id,
              ...updatedNote,
            },
          ];
        }
      })
      .addCase(updateGuestProfileNote.rejected, (state, action) => {
        state.updateGuestProfileNoteLoading = false;
        state.updateGuestProfileNoteSuccess = false;
        state.updateGuestProfileNoteError =
          action.payload || "Failed to update guest profile note.";
      })
      // ===========================
      // GET REMOVED GUEST JOBS
      // ===========================

      .addCase(getRemovedGuestDeletionJobs.pending, (state, action) => {
        state.removedGuestsRequestId = action.meta.requestId;
        state.removedGuests = [];
        state.removedGuestsLoading = true;
        state.removedGuestsError = null;
      })
      .addCase(getRemovedGuestDeletionJobs.fulfilled, (state, action) => {
        if (state.removedGuestsRequestId !== action.meta.requestId) return;
        state.removedGuestsLoading = false;

        state.removedGuests = action.payload?.items || [];
        state.removedGuestsPage = action.payload?.page || 0;
        state.removedGuestsPageSize = action.payload?.pageSize || 20;
        state.removedGuestsTotal = action.payload?.total || 0;
      })
      .addCase(getRemovedGuestDeletionJobs.rejected, (state, action) => {
        if (state.removedGuestsRequestId !== action.meta.requestId) return;
        state.removedGuestsLoading = false;
        state.removedGuestsError = action.payload;
      })

      // GET SUMMARY
      .addCase(getRemovedGuestDeletionSummary.pending, (state) => {
        state.removedGuestsSummaryLoading = true;
        state.removedGuestsSummaryError = null;
      })
      .addCase(getRemovedGuestDeletionSummary.fulfilled, (state, action) => {
        state.removedGuestsSummaryLoading = false;
        state.removedGuestsSummary = action.payload || null;
      })
      .addCase(getRemovedGuestDeletionSummary.rejected, (state, action) => {
        state.removedGuestsSummaryLoading = false;
        state.removedGuestsSummaryError = action.payload;
      })

      // RETRY JOB
      .addCase(retryGuestDeletionJob.pending, (state) => {
        state.retryGuestDeletionLoading = true;
        state.retryGuestDeletionError = null;
        state.retryGuestDeletionSuccess = false;
      })
      .addCase(retryGuestDeletionJob.fulfilled, (state, action) => {
        state.retryGuestDeletionLoading = false;
        state.retryGuestDeletionSuccess = true;
        state.retryGuestDeletionJobId = action.payload?.jobId || null;

        const jobId = action.payload?.jobId;

        const index = state.removedGuests.findIndex((item) => item.jobId === jobId);

        if (index !== -1) {
          state.removedGuests[index].status = action.payload?.status || "pending";

          state.removedGuests[index].progress = {
            completedSteps: action.payload?.completedSteps || 0,
            totalSteps: state.removedGuests[index]?.progress?.totalSteps || 0,
          };
        }
      })
      .addCase(retryGuestDeletionJob.rejected, (state, action) => {
        state.retryGuestDeletionLoading = false;
        state.retryGuestDeletionError = action.payload;
      });
  },
});

export const {
  resetGuestProfileView,
  resetGuestState,
  clearGuestError,
  clearGuestFlags,
  resetFlagUpdateState,
  resetNotesState,
  resetCreateNoteState,
  resetGuestLibraryState,
  resetGuestProfileState,
  resetDeleteGuestState,
  resetCreateGuestProfileNoteState,
  resetUpdateGuestProfileNoteState,
  resetRemovedGuestsState,
  resetRetryGuestDeletionState,
} = guestSlice.actions;
export default guestSlice.reducer;
